import { useEffect } from 'react';
import { DiagnosticPanelState, PlaybackState } from '../../types/enums';
import type { Turn } from '../../types/ski-data';
import { usePlaybackMachine } from '../../state-machines/usePlaybackMachine';
import { useDiagnosticPanel } from '../../state-machines/useDiagnosticPanel';
import CoachScript from './CoachScript';

/**
 * DiagnosticPanel - Bottom 200px of the R2 middle column (SM-4).
 *
 * Only visible when the playback machine is in `PausedInTurn`. It owns the
 * SM-4 diagnostic-panel state machine (via `useDiagnosticPanel`) and wires it
 * to SM-1 playback state:
 *   - PausedInTurn (turn entered)  → SHOW / UPDATE_TURN
 *   - leaves PausedInTurn          → HIDE
 *
 * Three sections:
 *   1. Coach script (CoachScript) — left-border ice narrative.
 *   2. Pressure main line — outside-pressure ratio + mini COP trace.
 *   3. Supporting metrics — edge angle avg, sync score, roundness badges.
 */
interface DiagnosticPanelProps {
  turns: Turn[];
}

export default function DiagnosticPanel({ turns }: DiagnosticPanelProps) {
  const playback = usePlaybackMachine();
  const diag = useDiagnosticPanel();

  const pbState = playback.state.state;
  const currentTurnIndex = playback.state.currentTurnIndex;
  const diagState = diag.state.state;
  const diagTurn = diag.state.currentTurnIndex;

  // Wire SM-4 → SM-1: drive show / hide / updateTurn from playback state.
  // The action callbacks (show/hide/updateTurn) are stable, and we read the
  // latest diag state values via the deps so transitions don't loop.
  useEffect(() => {
    const isPausedInTurn =
      pbState === PlaybackState.PausedInTurn && currentTurnIndex != null;

    if (isPausedInTurn) {
      if (diagState === DiagnosticPanelState.Hidden) {
        diag.show(currentTurnIndex);
      } else if (
        diagState === DiagnosticPanelState.Visible &&
        diagTurn !== currentTurnIndex
      ) {
        diag.updateTurn(currentTurnIndex);
      }
    } else if (
      diagState === DiagnosticPanelState.Visible ||
      diagState === DiagnosticPanelState.Updating
    ) {
      diag.hide();
    }
  }, [pbState, currentTurnIndex, diagState, diagTurn, diag]);

  const isVisible = diagState !== DiagnosticPanelState.Hidden;
  const turn =
    diagTurn != null
      ? turns.find((t) => t.turn_index === diagTurn) ?? null
      : null;

  return (
    <div
      style={{
        height: '200px',
        flexShrink: 0,
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--bg-card)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {isVisible && turn ? (
        <div
          ref={diag.panelRef}
          style={{
            height: '100%',
            padding: 'var(--space-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
          }}
        >
          <CoachScript turn={turn} />
          <PressureMainLine turn={turn} />
          <SupportingMetrics turn={turn} />
        </div>
      ) : (
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          暂停在弯内以查看逐弯诊断
        </div>
      )}
    </div>
  );
}

// ===== Internal: Pressure main line =====

function PressureMainLine({ turn }: { turn: Turn }) {
  const ratio = turn.outside_pressure_ratio;
  const ratioPct = Math.round(ratio * 100);
  const color =
    ratio >= 0.75
      ? 'var(--status-good)'
      : ratio >= 0.6
        ? 'var(--status-ok)'
        : 'var(--status-bad)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-md)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-tertiary)',
          flexShrink: 0,
        }}
      >
        外板占比
      </span>
      <span
        style={{
          fontSize: 'var(--fs-md)',
          fontFamily: 'var(--font-mono)',
          color,
          fontWeight: 'var(--fw-semibold)',
          flexShrink: 0,
        }}
      >
        {ratioPct}%
      </span>
      <MiniCopTrace turn={turn} />
    </div>
  );
}

/** Mini COP trace rendered as an SVG polyline scaled to fit. */
function MiniCopTrace({ turn }: { turn: Turn }) {
  const trace = turn.cop_trace;
  const W = 120;
  const H = 24;

  if (trace.length < 2) {
    return (
      <div
        style={{
          flex: 1,
          height: H,
          background: 'var(--bg-surface-1)',
          borderRadius: 'var(--radius-sm)',
        }}
      />
    );
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of trace) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;

  const points = trace
    .map((p) => {
      const x = ((p.x - minX) / rangeX) * W;
      const y = H - ((p.y - minY) / rangeY) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <div
      style={{
        flex: 1,
        height: H,
        background: 'var(--bg-surface-1)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
      }}
    >
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
      >
        <polyline
          points={points}
          fill="none"
          stroke="var(--color-ice)"
          strokeWidth={1}
        />
      </svg>
    </div>
  );
}

// ===== Internal: Supporting metrics =====

function SupportingMetrics({ turn }: { turn: Turn }) {
  const items = [
    {
      label: '立刃均值',
      value: `${Math.round(turn.edge_angle_avg)}°`,
      good: turn.edge_angle_avg >= 25,
      ok: turn.edge_angle_avg >= 18,
    },
    {
      label: '同步率',
      value: `${Math.round(turn.edging_sync_score)}`,
      good: turn.edging_sync_score >= 80,
      ok: turn.edging_sync_score >= 60,
    },
    {
      label: '圆润度',
      value: `${Math.round(turn.turn_roundness)}`,
      good: turn.turn_roundness >= 80,
      ok: turn.turn_roundness >= 60,
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
      {items.map((it) => {
        const color = it.good
          ? 'var(--status-good)'
          : it.ok
            ? 'var(--status-ok)'
            : 'var(--status-bad)';
        return (
          <span
            key={it.label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-secondary)',
              background: 'var(--bg-surface-1)',
              padding: '2px 8px',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: color,
                flexShrink: 0,
              }}
            />
            {it.label}
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-primary)',
              }}
            >
              {it.value}
            </span>
          </span>
        );
      })}
    </div>
  );
}
